const RELEASE_STATUS_DEFAULT = 'all';

const GAMES_FILTER_DEFAULTS = Object.freeze({
    searchTerm: '',
    platform: 'all',
    genre: 'all',
    sortBy: 'release_date_desc',
    theme: 'all',
    category: 'all',
    gameMode: 'all',
    developer: 'all',
    publisher: 'all',
    franchise: 'all',
    series: 'all',
    gameEngine: 'all',
    playerPerspective: 'all',
    ageRating: 'all',
    releaseYear: 'all',
    releaseStatus: RELEASE_STATUS_DEFAULT,
});

function normalizeReleaseYear(value) {
    if (value === undefined || value === null) return 'all';
    if (value === '') return 'all';
    if (value === 'all') return 'all';
    return String(value);
}

export function normalizeGamesFilters(filters = {}) {
    const base = { ...GAMES_FILTER_DEFAULTS, ...filters };

    return {
        searchTerm: typeof base.searchTerm === 'string'
            ? base.searchTerm
            : GAMES_FILTER_DEFAULTS.searchTerm,
        platform: base.platform || GAMES_FILTER_DEFAULTS.platform,
        genre: base.genre || GAMES_FILTER_DEFAULTS.genre,
        sortBy: base.sortBy || GAMES_FILTER_DEFAULTS.sortBy,
        theme: base.theme || GAMES_FILTER_DEFAULTS.theme,
        category: base.category || GAMES_FILTER_DEFAULTS.category,
        gameMode: base.gameMode || GAMES_FILTER_DEFAULTS.gameMode,
        developer: base.developer || GAMES_FILTER_DEFAULTS.developer,
        publisher: base.publisher || GAMES_FILTER_DEFAULTS.publisher,
        franchise: base.franchise || GAMES_FILTER_DEFAULTS.franchise,
        series: base.series || GAMES_FILTER_DEFAULTS.series,
        gameEngine: base.gameEngine || GAMES_FILTER_DEFAULTS.gameEngine,
        playerPerspective: base.playerPerspective || GAMES_FILTER_DEFAULTS.playerPerspective,
        ageRating: base.ageRating || GAMES_FILTER_DEFAULTS.ageRating,
        releaseYear: normalizeReleaseYear(base.releaseYear),
        releaseStatus: base.releaseStatus || GAMES_FILTER_DEFAULTS.releaseStatus,
    };
}

export function filtersFromQueryParams(search) {
    const params = new URLSearchParams(search);

    return normalizeGamesFilters({
        searchTerm: params.get('search') ?? undefined,
        platform: params.get('platform') ?? undefined,
        genre: params.get('genre') ?? undefined,
        sortBy: params.get('sortBy') ?? undefined,
        theme: params.get('theme') ?? undefined,
        category: params.get('category') ?? undefined,
        gameMode: params.get('gameMode') ?? undefined,
        developer: params.get('developer') ?? undefined,
        publisher: params.get('publisher') ?? undefined,
        franchise: params.get('franchise') ?? undefined,
        series: params.get('series') ?? undefined,
        gameEngine: params.get('gameEngine') ?? undefined,
        playerPerspective: params.get('playerPerspective') ?? undefined,
        ageRating: params.get('ageRating') ?? undefined,
        releaseYear: params.has('releaseYear') ? params.get('releaseYear') : undefined,
        releaseStatus: params.get('releaseStatus') ?? undefined,
    });
}

export function filtersToQueryParams(filters = {}, { includeDefaults = false } = {}) {
    const normalized = normalizeGamesFilters(filters);
    const params = new URLSearchParams();

    const searchTerm = typeof normalized.searchTerm === 'string' ? normalized.searchTerm : '';
    const trimmedSearchTerm = searchTerm.trim();

    if (trimmedSearchTerm) {
        params.set('search', searchTerm);
    } else if (includeDefaults) {
        params.set('search', '');
    }

    const maybeSet = (key, value, defaultValue, { allowAllValue = false } = {}) => {
        if (includeDefaults || value !== defaultValue) {
            const isAll = value === 'all';
            if (value && (!isAll || allowAllValue)) {
                params.set(key, value);
            } else if (includeDefaults) {
                params.set(key, value);
            }
        }
    };

    maybeSet('platform', normalized.platform, GAMES_FILTER_DEFAULTS.platform);
    maybeSet('genre', normalized.genre, GAMES_FILTER_DEFAULTS.genre);
    maybeSet('sortBy', normalized.sortBy, GAMES_FILTER_DEFAULTS.sortBy);
    maybeSet('theme', normalized.theme, GAMES_FILTER_DEFAULTS.theme);
    maybeSet('category', normalized.category, GAMES_FILTER_DEFAULTS.category);
    maybeSet('gameMode', normalized.gameMode, GAMES_FILTER_DEFAULTS.gameMode);
    maybeSet('developer', normalized.developer, GAMES_FILTER_DEFAULTS.developer);
    maybeSet('publisher', normalized.publisher, GAMES_FILTER_DEFAULTS.publisher);
    maybeSet('franchise', normalized.franchise, GAMES_FILTER_DEFAULTS.franchise);
    maybeSet('series', normalized.series, GAMES_FILTER_DEFAULTS.series);
    maybeSet('gameEngine', normalized.gameEngine, GAMES_FILTER_DEFAULTS.gameEngine);
    maybeSet('playerPerspective', normalized.playerPerspective, GAMES_FILTER_DEFAULTS.playerPerspective);
    maybeSet('ageRating', normalized.ageRating, GAMES_FILTER_DEFAULTS.ageRating);
    maybeSet('releaseYear', normalized.releaseYear, GAMES_FILTER_DEFAULTS.releaseYear);
    maybeSet('releaseStatus', normalized.releaseStatus, GAMES_FILTER_DEFAULTS.releaseStatus, { allowAllValue: true });

    return params;
}

export function createGamesQueryKey(filters = {}) {
    const normalized = normalizeGamesFilters(filters);
    const searchTerm = typeof normalized.searchTerm === 'string'
        ? normalized.searchTerm.trim()
        : GAMES_FILTER_DEFAULTS.searchTerm;

    return [
        'games',
        {
            searchTerm,
            platform: normalized.platform,
            genre: normalized.genre,
            sortBy: normalized.sortBy,
            theme: normalized.theme,
            category: normalized.category,
            gameMode: normalized.gameMode,
            developer: normalized.developer,
            publisher: normalized.publisher,
            franchise: normalized.franchise,
            series: normalized.series,
            gameEngine: normalized.gameEngine,
            playerPerspective: normalized.playerPerspective,
            ageRating: normalized.ageRating,
            releaseYear: normalized.releaseYear,
            releaseStatus: normalized.releaseStatus,
        },
    ];
}

export function createGamesSearchString(partialFilters = {}) {
    const params = filtersToQueryParams(partialFilters);
    const query = params.toString();
    return query ? `?${query}` : '';
}

export { GAMES_FILTER_DEFAULTS };
